import apiClient from "./client";
import { fetchAllPages } from "./pagination";
import { buildJsonApiQuery } from '@/utils/jsonapi';

/**
 * Data Entry Forms API module
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */
const formsAPI = {
  /**
   * Get one page of forms with optional filtering
   * @param {Object} [queryOptions] - JSON:API query options
   * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { is_active: true })
   * @param {Array<string>|string} [queryOptions.sort] - Sort fields (e.g., ['name', '-created_at'])
   * @param {Array<string>|string} [queryOptions.include] - Relations to include
   * @param {number} [queryOptions.page] - Page number
   * @param {number} [queryOptions.pageSize] - Items per page
   * @returns {Promise<Object>} JSON:API response with data, meta, links
   */
  getAll: async (queryOptions = {}, requestOptions = {}) => {
    const params = buildJsonApiQuery(queryOptions);
    const response = await apiClient.get("/forms/", { ...requestOptions, params });
    return response.data;
  },

  /** Load every form for the data-entry library's local search and pins. */
  getAllPages: (queryOptions = {}, requestOptions = {}) =>
    fetchAllPages(formsAPI.getAll, queryOptions, requestOptions),

  /**
   * Get a specific form by ID
   * @param {string} formId - Form UUID
   * @param {Object} [queryOptions] - JSON:API query options
   * @returns {Promise<Object>} JSON:API response with form data
   */
  getById: async (formId, queryOptions = {}) => {
    // Default to including form_metrics with their metrics, partition configs, and partition values
    const defaultIncludes = 'form_metrics.metric.category,form_metrics.metric.partition_configurations.partition_type.partition_values';
    const params = buildJsonApiQuery({
      ...queryOptions,
      include: queryOptions.include || defaultIncludes
    });
    const response = await apiClient.get(`/forms/${formId}/`, { params });
    return response.data;
  },

  /**
   * Create a new form
   * @param {Object} data - Form data
   * @param {string} data.name - Form name
   * @param {string} [data.description] - Form description
   * @param {string} [data.display_mode] - Display mode ('simple' or 'full')
   * @param {Array<Object>} data.metrics - Array of metric configs [{metric_id, sort_order}]
   * @returns {Promise<Object>} JSON:API response with created form
   */
  create: async (data) => {
    const response = await apiClient.post("/forms/", data);
    return response.data;
  },

  /**
   * Update a form
   * @param {string} formId - Form UUID
   * @param {Object} data - Updated form data
   * @param {Object} [queryOptions] - JSON:API query options
   * @returns {Promise<Object>} JSON:API response with updated form
   */
  update: async (formId, data, queryOptions = {}) => {
    const params = buildJsonApiQuery(queryOptions);
    const response = await apiClient.patch(`/forms/${formId}/`, data, { params });
    return response.data;
  },

  /**
   * Delete (deactivate) a form
   * @param {string} formId - Form UUID
   * @returns {Promise<void>}
   */
  delete: async (formId) => {
    await apiClient.delete(`/forms/${formId}/`);
  },

  /**
   * Duplicate a form
   * @param {string} formId - Source form UUID
   * @param {Object} data - Duplicate options
   * @param {string} [data.name] - New form name (defaults to "Original Name (Copy)")
   * @returns {Promise<Object>} JSON:API response with duplicated form
   */
  duplicate: async (formId, data = {}) => {
    const response = await apiClient.post(`/forms/${formId}/duplicate/`, data);
    return response.data;
  },

  /**
   * The church's starter weekly form: its oldest active form, or a new
   * "Weekly numbers" form of the standard measurements (administrators only)
   * @returns {Promise<Object>} JSON:API response with the form
   */
  createStarter: async () => {
    const response = await apiClient.post("/forms/starter/");
    return response.data;
  },

  /**
   * Reorder metrics within a form
   * @param {string} formId - Form UUID
   * @param {Array<string>} metricIds - Ordered array of metric UUIDs
   * @returns {Promise<Object>} JSON:API response with updated form
   */
  reorderMetrics: async (formId, metricIds) => {
    const response = await apiClient.patch(`/forms/${formId}/reorder-metrics/`, {
      metric_ids: metricIds
    });
    return response.data;
  },

  /**
   * Get available metrics for form creation, grouped by partition type signature
   * @returns {Promise<Object>} Response with metrics grouped by partition compatibility
   */
  getAvailableMetrics: async () => {
    const response = await apiClient.get("/forms/available-metrics/");
    return response.data;
  },
};

export default formsAPI;
